import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/auth/AuthContext';
import { useProfile } from '@contexts/profile/ProfileContext';
import { matchesRoute } from '@/constants/layoutRoutes';

export interface AuthLayoutContextType {
  isNavbarVisible: boolean;
  isHeaderVisible: boolean;
  isExiting: boolean;
  animationCompleted: boolean;
  shouldApplyPadding: boolean;
  /** True from the render a route changes the nav/header until Layout reports their animations done. */
  isShellSettling: boolean;
  markShellSettled: (shell: ShellVisibility) => void;
  /** Called by Layout once the nav and header have finished sliding out; runs the pending exit callback. */
  completeExit: () => void;
  showNavbarAndHeader: () => void;
  hideNavbarAndHeader: (callback?: () => void) => void;
  triggerExit: (callback: () => void) => void;
}

export interface ShellVisibility {
  navbar: boolean;
  header: boolean;
}

interface AuthLayoutProviderProps {
  children: React.ReactNode;
  hiddenNavbarRoutes?: string[];
  hiddenHeaderRoutes?: string[];
}

const AuthLayoutContext = createContext<AuthLayoutContextType | undefined>(undefined);

// Default routes where navbar should be hidden
const DEFAULT_HIDDEN_NAVBAR_ROUTES = [
  '/purchase/treasurybill',
  '/purchase/confirmation',
  '/auth/login',
  '/auth/register',
  '/reset-password',
  '/forgot-password',
  '/business-select',
  '/wallet/add-money',
  '/wallet/withdraw',
  '/welcome',
  '/login',
  '/signup',
];

// Default routes where header should be hidden
const DEFAULT_HIDDEN_HEADER_ROUTES = [
  '/purchase/treasurybill',
  '/purchase/confirmation',
  '/auth/login',
  '/auth/register',
  '/business-select',
  '/reset-password',
  '/forgot-password',
  '/wallet/add-money',
  '/wallet/withdraw',
  '/welcome',
  '/login',
  '/signup',
];

export const AuthLayoutProvider: React.FC<AuthLayoutProviderProps> = ({
  children,
  hiddenNavbarRoutes = DEFAULT_HIDDEN_NAVBAR_ROUTES,
  hiddenHeaderRoutes = DEFAULT_HIDDEN_HEADER_ROUTES,
}) => {
  const { isAuthenticated } = useAuth();
  const { currentBusinessId } = useProfile();
  const location = useLocation();

  const [isExiting, setIsExiting] = useState(false);
  const pendingExitRef = useRef<(() => void) | null>(null);
  const [animationCompleted, setAnimationCompleted] = useState(true);
  const [settledShell, setSettledShell] = useState<ShellVisibility>({
    navbar: false,
    header: false,
  });

  const shouldHideNavbar = useCallback(
    (pathname: string) => matchesRoute(pathname, hiddenNavbarRoutes),
    [hiddenNavbarRoutes],
  );

  const shouldHideHeader = useCallback(
    (pathname: string) => matchesRoute(pathname, hiddenHeaderRoutes),
    [hiddenHeaderRoutes],
  );

  // Only show navbar/header if:
  // 1. User is authenticated
  // 2. User has selected a business
  // 3. Current route doesn't explicitly hide them
  const shouldShowNavbar = Boolean(
    isAuthenticated && currentBusinessId && !shouldHideNavbar(location.pathname),
  );
  const shouldShowHeader = Boolean(
    isAuthenticated && currentBusinessId && !shouldHideHeader(location.pathname),
  );

  // Derived rather than synced in an effect: an exit hides the shell on the very render it starts,
  // and the route can't flip it back on until the exit's own navigation has happened.
  const isNavbarVisible = shouldShowNavbar && !isExiting;
  const isHeaderVisible = shouldShowHeader && !isExiting;
  const shouldApplyPadding = isNavbarVisible && isHeaderVisible;

  const isShellSettling =
    shouldShowNavbar !== settledShell.navbar || shouldShowHeader !== settledShell.header;

  const markShellSettled = useCallback((shell: ShellVisibility) => {
    setSettledShell((current) =>
      current.navbar === shell.navbar && current.header === shell.header ? current : shell,
    );
  }, []);

  const showNavbarAndHeader = useCallback(() => {
    pendingExitRef.current = null;
    setIsExiting(false);
    setAnimationCompleted(false);

    // Mark animation as completed after entrance animations
    const timer = setTimeout(() => {
      setAnimationCompleted(true);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const triggerExit = useCallback((callback?: () => void) => {
    pendingExitRef.current = callback ?? null;
    setIsExiting(true);
  }, []);

  // Without a callback the shell stays hidden: nothing navigates away, so ending the exit would
  // only slide the nav straight back in on the same route.
  const completeExit = useCallback(() => {
    const callback = pendingExitRef.current;
    if (!callback) return;
    pendingExitRef.current = null;
    setIsExiting(false);
    callback();
  }, []);

  const value: AuthLayoutContextType = {
    isNavbarVisible,
    isHeaderVisible,
    isExiting,
    animationCompleted,
    shouldApplyPadding,
    isShellSettling,
    markShellSettled,
    completeExit,
    showNavbarAndHeader,
    hideNavbarAndHeader: triggerExit,
    triggerExit,
  };

  return <AuthLayoutContext.Provider value={value}>{children}</AuthLayoutContext.Provider>;
};

export const useAuthLayout = (): AuthLayoutContextType | null => {
  const context = useContext(AuthLayoutContext);
  // Return null if not within provider (when user is not authenticated)
  return context || null;
};

export const useAuthLayoutRequired = (): AuthLayoutContextType => {
  const context = useContext(AuthLayoutContext);
  if (context === undefined) {
    throw new Error('useAuthLayoutRequired must be used within an AuthLayoutProvider');
  }
  return context;
};

export default AuthLayoutContext;
